import React, { Suspense, useEffect, useState, useRef } from "react";

import homePagePayload from "../../config/homePayload";
import homePageAPI from "../../api/homePageApi";

import CouponFlash from "./CouponFlash";
import { Link } from "react-router-dom";
import { Row, Col, Container } from "react-bootstrap";

import {
  Swiper,
  SwiperSlide,
  Autoplay,
  Navigation,
  Pagination,
} from "./Shared/CustomSwiper";
import { FaBox, FaCheckCircle, FaHeadset } from "react-icons/fa";
import { Clock, Heart, ShieldCheck, Truck } from "lucide-react";

const Product = React.lazy(() => import("../products/Card"));
const Testimonials = React.lazy(() => import("../testimonials/Card"));
const CategoryCard = React.lazy(() => import("../categories/Card"));
const Blog = React.lazy(() => import("../blogs/Card"));
const Banner = React.lazy(() => import("./Banner"));
const BrandsCard = React.lazy(() => import("../brands/Card"));

function Home() {
  const [coupons, setCoupons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [products, setProducts] = useState([]);
  const [topSellerProducts, setTopSellerProducts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [topBanners, setTopBanners] = useState([]);
  const [middleBanners, setMiddleBanners] = useState([]);
  const firstMiddleBanner = middleBanners[0];
  const secondMiddleBanner = middleBanners[1];
  const [brands, setBrands] = useState([]);

  const parallaxRef = useRef(null);
  const [parallaxOffset, setParallaxOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!parallaxRef.current) return;
      const rect = parallaxRef.current.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const elementCenter = rect.top + rect.height / 2;
      const distance = viewportCenter - elementCenter;
      setParallaxOffset(distance * 0.15); // 0.15 = parallax speed
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    fetchHomePageDetails();
  }, []);

  const CACHE_TIME = 3 * 60 * 1000;

  const applyData = (data) => {
    setTopBanners(data?.banners?.filter((b) => b.position === "top"));
    setMiddleBanners(data?.banners?.filter((b) => b.position === "middle"));
    setCoupons(data?.coupons);
    setCategories(data?.categories);
    setTestimonials(data?.testimonials);
    setBlogs(data?.blogs);
    setProducts(data?.products);
    setTopSellerProducts(data?.topSellerProducts);
    setBrands(data?.brands);
  };

  const fetchHomePageDetails = async () => {
    try {
      const cached = sessionStorage.getItem("homeData");
      const parsed = cached ? JSON.parse(cached) : null;

      if (parsed) applyData(parsed.data);

      if (!parsed || Date.now() - parsed.timestamp > CACHE_TIME) {
        const res = await homePageAPI.homePage(homePagePayload);
        if (res.code === 200) {
          sessionStorage.setItem(
            "homeData",
            JSON.stringify({ data: res.data, timestamp: Date.now() }),
          );
          if (!parsed) applyData(res.data); // only update state if no cache was shown
        }
      }
    } catch (error) {
      console.error("Home API Error:", error);
    }
  };

  // featured section
  const features = [
    {
      id: 1,
      icon: <Truck size={40} strokeWidth={1.5} className="text-danger1" />,
      title: "Fast & Reliable Delivery",
      description: "Same day delivery in 50+ cities with real-time tracking.",
    },
    {
      id: 2,
      icon: <Heart size={40} strokeWidth={1.5} className="text-danger1" />,
      title: "Curated with Love",
      description: "Each hamper is hand-picked and styled to perfection.",
    },
    {
      id: 3,
      icon: (
        <ShieldCheck size={40} strokeWidth={1.5} className="text-danger1" />
      ),
      title: "Secure Payments",
      description: "100% safe checkout with multiple payment options.",
    },
    {
      id: 4,
      icon: <Clock size={40} strokeWidth={1.5} className="text-danger1" />,
      title: "24/7 Support",
      description: "Dedicated team to help you with your gifting needs.",
    },
  ];

  return (
    <>
      {/* category listing */}
      {categories && categories.length > 0 && (
        <section className="py-md-4 py-1">
          <Container fluid className="px-md-5 px-2">
            <div className="custom-category-wrapper">
              <Swiper
                modules={[Autoplay]}
                slidesPerView={1}
                spaceBetween={16}
                pagination={{ clickable: true }}
                autoplay={{ delay: 2000, disableOnInteraction: false }}
                loop={true}
                breakpoints={{
                  0: { slidesPerView: 1.1, spaceBetween: 12 },
                  576: { slidesPerView: 2, spaceBetween: 14 },
                  768: { slidesPerView: 3, spaceBetween: 16 },
                  1024: { slidesPerView: 4, spaceBetween: 18 },
                }}
                className="custom-category-swiper"
              >
                {categories.map((category, index) => (
                  <SwiperSlide key={index} className="custom-category-slide">
                    <CategoryCard item={category} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </Container>
        </section>
      )}

      {/* show banners */}
      {topBanners && topBanners.length > 0 && (
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          slidesPerView={1}
          pagination={{ clickable: true }}
          autoplay={{ delay: 3000 }}
          loop={true}
        >
          {topBanners.map((banner, index) => (
            <SwiperSlide key={index}>
              <Banner item={banner} />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
      {/* brands section */}
      {brands && brands.length > 0 && (
  <div className="ax-brand-marquee-wrapper py-4" data-aos="fade-up">
    <div className="ax-brand-marquee-track">
      {[...brands, ...brands].map((brand, index) => (
        <div className="ax-brand-marquee-item" key={index}>
          <BrandsCard item={brand} />
        </div>
      ))}
    </div>
  </div>
)}

      {/* Coupon Bar */}
      {coupons && coupons.length > 0 && (
        <section className="custom-coupon-section">
          <div className="custom-coupon-marquee-wrap">
            <div className="custom-coupon-marquee">
              {coupons.map((coupon, index) => (
                <CouponFlash key={coupon._id || index} item={coupon} />
              ))}

              {/* DUPLICATE FOR SMOOTH LOOP */}
              {coupons.map((coupon, index) => (
                <CouponFlash
                  key={`duplicate-${coupon._id || index}`}
                  item={coupon}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* bestsellers */}
      {products && products.length > 0 && (
        <section className="py-5 bg-light">
          <Container fluid className="px-md-5 px-2">
            <div className="d-flex justify-content-between align-items-end mb-4 px-2">
              <div>
                <h6
                  className="text-uppercase text-primary1 fw-semibold mb-1"
                  style={{ fontSize: "14px", letterSpacing: "2px" }}
                >
                  Our Favorites
                </h6>
                <h2
                  className="fw-bold m-0"
                  style={{
                    fontFamily: "var(--font-heading)",
                    fontSize: "32px",
                  }}
                >
                  Best Seller Hampers
                </h2>
              </div>
              <Link
                to="/shop"
                className="text-decoration-none fw-semibold"
                style={{ color: "var(--primary)" }}
              >
                View All →
              </Link>
            </div>
            <Row className="g-3">
              <Suspense fallback={<div></div>}>
                {products.map((product, index) => (
                  <Col
                    lg={3}
                    md={4}
                    sm={6}
                    xs={12}
                    key={[product._id || index]}
                  >
                    <Product item={product} />
                  </Col>
                ))}
              </Suspense>
            </Row>
          </Container>
        </section>
      )}

      {/* middle banners */}
      {firstMiddleBanner && (
        <div className="middle-banner" ref={parallaxRef}>
          <div
            className="middle-banner-bg"
            style={{
              backgroundImage: `url(${firstMiddleBanner.image})`,
              transform: `translateY(${parallaxOffset}px)`,
            }}
          />

          <div className="middle-banner-overlay" />

          <div className="middle-banner-content">
            <h2 className="spotlight-heading">{firstMiddleBanner.title}</h2>
            <p className="spotlight-description">
              {firstMiddleBanner.subTitle}
            </p>
            <Link to="/shop" className="spotlight-btn">
              Discover More
            </Link>
          </div>
        </div>
      )}

      {/* trending */}
      {topSellerProducts && topSellerProducts.length > 0 && (
        <section className="py-5 bg-light">
          <Container fluid className="px-md-5 px-2">
            <div className="d-flex justify-content-between align-items-end mb-4 px-2">
              <div>
                <h6
                  className="text-uppercase text-primary1 fw-semibold mb-1"
                  style={{ fontSize: "14px", letterSpacing: "2px" }}
                >
                  Our Favorites
                </h6>
                <h2
                  className="fw-bold m-0"
                  style={{
                    fontFamily: "var(--font-heading)",
                    fontSize: "32px",
                  }}
                >
                  Top Sellers
                </h2>
              </div>
              <Link
                to="/shop"
                className="text-decoration-none fw-semibold"
                style={{ color: "var(--primary)" }}
              >
                View All →
              </Link>
            </div>
            <Row className="g-3">
              <Suspense fallback={<div></div>}>
                {topSellerProducts.map((product, index) => (
                  <Col
                    lg={3}
                    md={6}
                    sm={6}
                    xs={12}
                    key={[product._id || index]}
                  >
                    <Product item={product} />
                  </Col>
                ))}
              </Suspense>
            </Row>
          </Container>
        </section>
      )}

      {testimonials && testimonials.length > 0 && (
        <section className="py-5 overflow-hidden">
          <div className="container">
            {/* Section Header */}
            <div className="text-left">
              <h6
                className="text-uppercase text-primary1 fw-semibold mb-1"
                style={{ fontSize: "14px", letterSpacing: "2px" }}
              >
                Testimonials
              </h6>
              <h2
                className="fw-bold m-0"
                style={{ fontFamily: "var(--font-heading)", fontSize: "32px" }}
              >
                What Our Customer Says
              </h2>
            </div>

            <Swiper
              modules={[Navigation, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              pagination={{ clickable: true }}
              autoplay={{ delay: 2500 }}
              breakpoints={{
                0: { slidesPerView: 1.2 },
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="testimonial-swiper"
            >
              <Suspense fallback={<div></div>}>
                {testimonials.map((testimonial, index) => (
                  <SwiperSlide key={index}>
                    <Testimonials item={testimonial} />
                  </SwiperSlide>
                ))}
              </Suspense>
            </Swiper>
          </div>
        </section>
      )}

      {blogs && blogs.length > 0 && (
        <section className="blog-section bg-light py-5">
          <Container>
            <div className="d-flex justify-content-between align-items-end mb-4 px-2">
              <div>
                <h6
                  className="text-uppercase text-primary1 fw-semibold mb-1"
                  style={{ fontSize: "14px", letterSpacing: "2px" }}
                >
                  Latest Stories
                </h6>
                <h2
                  className="fw-bold m-0"
                  style={{
                    fontFamily: "var(--font-heading)",
                    fontSize: "32px",
                  }}
                >
                  Our Latest Stories
                </h2>
              </div>
              <Link
                to="/blog"
                className="text-decoration-none fw-semibold"
                style={{ color: "var(--primary)" }}
              >
                View All →
              </Link>
            </div>

            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              pagination={{ clickable: true }}
              breakpoints={{
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="blog-swiper"
            >
              <Suspense fallback={<div></div>}>
                {blogs.map((blog, index) => (
                  <SwiperSlide key={blog.id}>
                    <Blog item={blog} />
                  </SwiperSlide>
                ))}
              </Suspense>
            </Swiper>
          </Container>
        </section>
      )}

      {/* featured section */}
      <section className="py-5 custom-featured-section border-top border-bottom">
        <div className="container">
          <div className="row g-4 justify-content-center">
            {features.map((item) => (
              <div className="col-6 col-md-3 text-center" key={item.id}>
                <div className="why-card p-3 transition-all">
                  <div className="icon-wrapper mb-3 d-inline-block p-3 rounded-circle bg-light shadow-sm">
                    {item.icon}
                  </div>
                  <h6
                    className="fw-bold text-white mb-2"
                    style={{ fontSize: "15px" }}
                  >
                    {item.title}
                  </h6>
                  <p
                    className="text-white mb-0"
                    style={{ fontSize: "12px", lineHeight: "1.6" }}
                  >
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
